/**
 * Cohort Graph Domain Model
 *
 * This model is intentionally aligned with src/data/cohorts.json field names
 * (name, description, includeConditions, excludeConditions, patientCount, etc.)
 * while extending it with graph-specific parent/child relationships.
 */

import { GRAPH_ACTIONS } from './graphActions';

export { GRAPH_ACTIONS } from './graphActions';

const TODAY = () => new Date().toISOString().slice(0, 10);

/**
 * @typedef {'AND'|'OR'} LogicalOperator
 */

/**
 * @typedef {Object} CohortCondition
 * @property {number|null} parameterId
 * @property {string} parameterName
 * @property {string} operator
 * @property {string} value
 * @property {LogicalOperator} logicalOperator
 */

/**
 * @typedef {'pending'|'unedited'|'edited'|'executing'|'executed'|'completed'|'error'} CohortNodeStatus
 */

/**
 * @typedef {Object} GraphNode
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {string|null} parentId
 * @property {string[]} childIds
 * @property {CohortCondition[]} includeConditions
 * @property {CohortCondition[]} excludeConditions
 * @property {number|null} patientCount
 * @property {number} usageCount
 * @property {boolean} isPublic
 * @property {string|null} parameterListId
 * @property {string|null} parameterListName
 * @property {string} createdAt
 * @property {string} createdBy
 * @property {string} lastModified
 * @property {boolean} isExecuted
 * @property {CohortNodeStatus} status
 */

/**
 * @typedef {Object} GraphState
 * @property {Object.<string, GraphNode>} nodesById
 * @property {string|null} rootNodeId
 * @property {Object} metadata
 * @property {string} metadata.name
 * @property {string} metadata.createdAt
 * @property {string} metadata.updatedAt
 * @property {string} metadata.source
 * @property {string|null} metadata.breakpointNodeId
 * @property {string|null} lastError
 */

const clone = (value) => JSON.parse(JSON.stringify(value));

const normalizeBreakpointNodeId = (rawBreakpointNodeId, nodesById) => {
  if (rawBreakpointNodeId === null || rawBreakpointNodeId === undefined) return null;
  const normalizedId = String(rawBreakpointNodeId);
  return nodesById?.[normalizedId] ? normalizedId : null;
};

const normalizeCondition = (raw) => {
  const parameterName = String(raw?.parameterName || raw?.field || raw?.name || '').trim();
  const operator = String(raw?.operator || '=').trim();
  const value = String(raw?.value || '').trim();
  const logicalOperator = String(raw?.logicalOperator || 'AND').toUpperCase() === 'OR' ? 'OR' : 'AND';

  return {
    parameterId: Number.isFinite(raw?.parameterId) ? Number(raw.parameterId) : null,
    parameterName,
    operator,
    value,
    logicalOperator,
  };
};

const normalizeConditions = (conditions) => {
  if (!Array.isArray(conditions)) return [];
  return conditions
    .map((condition) => normalizeCondition(condition))
    .filter((condition) => condition.parameterName.length > 0 || condition.value.length > 0);
};

const normalizeStatus = (status, isExecuted) => {
  if (
    status === 'pending' ||
    status === 'unedited' ||
    status === 'edited' ||
    status === 'executing' ||
    status === 'executed' ||
    status === 'completed' ||
    status === 'error'
  ) {
    return status;
  }
  return isExecuted ? 'executed' : 'unedited';
};

export const createEmptyGraphState = (name = 'Cohort Graph Draft') => {
  const today = TODAY();
  return {
    nodesById: {},
    rootNodeId: null,
    metadata: {
      name,
      createdAt: today,
      updatedAt: today,
      source: 'draft',
      breakpointNodeId: null,
    },
    lastError: null,
  };
};

export const createGraphNode = (id, overrides = {}) => {
  const today = TODAY();
  const includeConditions = normalizeConditions(overrides.includeConditions);
  const excludeConditions = normalizeConditions(overrides.excludeConditions);
  const isExecuted = Boolean(overrides.isExecuted) || Number.isFinite(overrides.patientCount);

  return {
    id,
    name: String(overrides.name || 'New Cohort'),
    description: String(overrides.description || ''),
    parentId: overrides.parentId ?? null,
    childIds: Array.isArray(overrides.childIds) ? [...new Set(overrides.childIds.map(String))] : [],
    includeConditions,
    excludeConditions,
    patientCount: Number.isFinite(overrides.patientCount) ? Number(overrides.patientCount) : null,
    usageCount: Number.isFinite(overrides.usageCount) ? Number(overrides.usageCount) : 0,
    isPublic: Boolean(overrides.isPublic),
    parameterListId: overrides.parameterListId ?? null,
    parameterListName: overrides.parameterListName ?? null,
    createdAt: String(overrides.createdAt || today),
    createdBy: String(overrides.createdBy || 'Current User'),
    lastModified: String(overrides.lastModified || today),
    isExecuted,
    status: normalizeStatus(overrides.status, isExecuted),
  };
};

const validateReferences = (nodesById) => {
  const errors = [];
  const nodeIds = new Set(Object.keys(nodesById));

  Object.values(nodesById).forEach((node) => {
    if (node.parentId && !nodeIds.has(node.parentId)) {
      errors.push(`Node ${node.id} references missing parent ${node.parentId}.`);
    }
    (node.childIds || []).forEach((childId) => {
      if (!nodeIds.has(childId)) {
        errors.push(`Node ${node.id} references missing child ${childId}.`);
        return;
      }
      const child = nodesById[childId];
      if (child.parentId !== node.id) {
        errors.push(`Node ${childId} parentId mismatch for parent ${node.id}.`);
      }
    });
  });

  return errors;
};

const collectRoots = (nodesById) =>
  Object.values(nodesById)
    .filter((node) => node.parentId === null)
    .map((node) => node.id);

const validateAcyclicAndConnected = (nodesById, rootId) => {
  const errors = [];
  if (!rootId) return errors;

  const visiting = new Set();
  const visited = new Set();

  const dfs = (nodeId) => {
    if (visiting.has(nodeId)) {
      errors.push(`Cycle detected at node ${nodeId}.`);
      return;
    }
    if (visited.has(nodeId)) return;

    visiting.add(nodeId);
    const node = nodesById[nodeId];
    if (!node) {
      errors.push(`Missing node ${nodeId} during traversal.`);
      visiting.delete(nodeId);
      return;
    }
    (node.childIds || []).forEach((childId) => dfs(childId));
    visiting.delete(nodeId);
    visited.add(nodeId);
  };

  dfs(rootId);

  const totalNodes = Object.keys(nodesById).length;
  if (visited.size !== totalNodes) {
    errors.push(`Graph is disconnected: reachable ${visited.size}/${totalNodes} nodes from root.`);
  }

  return errors;
};

export const validateGraphState = (state) => {
  const nodesById = state?.nodesById || {};
  const nodeIds = Object.keys(nodesById);
  const errors = [];

  if (nodeIds.length === 0) {
    if (state?.rootNodeId !== null) {
      errors.push('rootNodeId must be null when graph is empty.');
    }
    return { valid: errors.length === 0, errors };
  }

  errors.push(...validateReferences(nodesById));

  const roots = collectRoots(nodesById);
  if (roots.length !== 1) {
    errors.push(`Graph must have exactly one root, found ${roots.length}.`);
  } else if (state.rootNodeId !== roots[0]) {
    errors.push(`rootNodeId mismatch (expected ${roots[0]}, got ${state.rootNodeId || 'null'}).`);
  }

  if (roots.length === 1) {
    errors.push(...validateAcyclicAndConnected(nodesById, roots[0]));
  }

  return { valid: errors.length === 0, errors };
};

const applyValidated = (prevState, candidateState, failurePrefix) => {
  const normalizedBreakpointNodeId = normalizeBreakpointNodeId(
    candidateState?.metadata?.breakpointNodeId,
    candidateState?.nodesById,
  );
  const normalizedCandidate = {
    ...candidateState,
    metadata: {
      ...candidateState.metadata,
      breakpointNodeId: normalizedBreakpointNodeId,
    },
  };

  const validation = validateGraphState(normalizedCandidate);
  if (!validation.valid) {
    return {
      ...prevState,
      lastError: `${failurePrefix}: ${validation.errors.join(' ')}`,
    };
  }
  return {
    ...normalizedCandidate,
    lastError: null,
  };
};

export const reduceGraphAction = (prevState, action) => {
  if (!action || !action.type) {
    return {
      nextState: {
        ...prevState,
        lastError: 'Invalid action payload.',
      },
      valid: false,
    };
  }

  const state = clone(prevState);
  const today = TODAY();

  switch (action.type) {
    case GRAPH_ACTIONS.SET_GRAPH: {
      const next = normalizeGraphState(action.payload);
      const merged = {
        ...next,
        metadata: {
          ...next.metadata,
          updatedAt: today,
        },
      };
      const resolved = applyValidated(state, merged, 'SET_GRAPH failed validation');
      return { nextState: resolved, valid: resolved.lastError === null };
    }

    case GRAPH_ACTIONS.ADD_NODE: {
      const payload = action.payload || {};
      const parentId = payload.parentId ?? null;
      const insertBeforeChildId = payload.insertBeforeChildId ?? null;
      const node = createGraphNode(payload.node?.id || payload.id, payload.node || {});

      if (!node.id) {
        return {
          nextState: { ...state, lastError: 'ADD_NODE requires a node id.' },
          valid: false,
        };
      }
      if (state.nodesById[node.id]) {
        return {
          nextState: { ...state, lastError: `ADD_NODE failed: node ${node.id} already exists.` },
          valid: false,
        };
      }

      const nextNodes = clone(state.nodesById);
      node.parentId = parentId;
      nextNodes[node.id] = node;

      let rootNodeId = state.rootNodeId;

      if (parentId === null) {
        if (Object.keys(nextNodes).length > 1) {
          return {
            nextState: { ...state, lastError: 'ADD_NODE failed: graph already has nodes, cannot add second root.' },
            valid: false,
          };
        }
        rootNodeId = node.id;
      } else {
        const parent = nextNodes[parentId];
        if (!parent) {
          return {
            nextState: { ...state, lastError: `ADD_NODE failed: parent ${parentId} not found.` },
            valid: false,
          };
        }

        if (insertBeforeChildId) {
          const child = nextNodes[insertBeforeChildId];
          if (!child) {
            return {
              nextState: { ...state, lastError: `ADD_NODE failed: child ${insertBeforeChildId} not found for insert.` },
              valid: false,
            };
          }
          if (child.parentId !== parentId) {
            return {
              nextState: {
                ...state,
                lastError: `ADD_NODE failed: child ${insertBeforeChildId} is not linked to parent ${parentId}.`,
              },
              valid: false,
            };
          }

          parent.childIds = parent.childIds.map((childId) => (childId === child.id ? node.id : childId));
          node.childIds = [child.id];
          child.parentId = node.id;
          child.lastModified = today;
        } else {
          parent.childIds = [...parent.childIds, node.id];
        }

        parent.lastModified = today;
      }

      const candidate = {
        ...state,
        nodesById: nextNodes,
        rootNodeId,
        metadata: {
          ...state.metadata,
          updatedAt: today,
        },
      };
      const resolved = applyValidated(state, candidate, 'ADD_NODE failed validation');
      return { nextState: resolved, valid: resolved.lastError === null };
    }

    case GRAPH_ACTIONS.UPDATE_NODE: {
      const { nodeId, patch = {} } = action.payload || {};
      if (!nodeId || !state.nodesById[nodeId]) {
        return {
          nextState: { ...state, lastError: `UPDATE_NODE failed: node ${nodeId || 'unknown'} not found.` },
          valid: false,
        };
      }

      const nextNodes = clone(state.nodesById);
      const current = nextNodes[nodeId];

      const includeConditions = patch.includeConditions ? normalizeConditions(patch.includeConditions) : current.includeConditions;
      const excludeConditions = patch.excludeConditions ? normalizeConditions(patch.excludeConditions) : current.excludeConditions;

      nextNodes[nodeId] = {
        ...current,
        ...patch,
        name: patch.name !== undefined ? String(patch.name) : current.name,
        includeConditions,
        excludeConditions,
        status: normalizeStatus(patch.status !== undefined ? patch.status : current.status, patch.isExecuted ?? current.isExecuted),
        patientCount:
          patch.patientCount !== undefined
            ? Number.isFinite(patch.patientCount)
              ? Number(patch.patientCount)
              : null
            : current.patientCount,
        isExecuted:
          patch.isExecuted !== undefined
            ? Boolean(patch.isExecuted)
            : patch.patientCount !== undefined
              ? Number.isFinite(patch.patientCount)
              : current.isExecuted,
        lastModified: today,
      };

      const candidate = {
        ...state,
        nodesById: nextNodes,
        metadata: {
          ...state.metadata,
          updatedAt: today,
        },
      };
      const resolved = applyValidated(state, candidate, 'UPDATE_NODE failed validation');
      return { nextState: resolved, valid: resolved.lastError === null };
    }

    case GRAPH_ACTIONS.DELETE_NODE: {
      const { nodeId, cascade = false } = action.payload || {};
      if (!nodeId || !state.nodesById[nodeId]) {
        return {
          nextState: { ...state, lastError: `DELETE_NODE failed: node ${nodeId || 'unknown'} not found.` },
          valid: false,
        };
      }

      const nextNodes = clone(state.nodesById);
      const toDelete = new Set();
      const stack = [nodeId];

      while (stack.length > 0) {
        const id = stack.pop();
        if (!id || toDelete.has(id)) continue;
        toDelete.add(id);
        if (cascade) {
          (nextNodes[id]?.childIds || []).forEach((childId) => stack.push(childId));
        }
      }

      const node = nextNodes[nodeId];
      if (!cascade && (node.childIds || []).length > 0) {
        return {
          nextState: {
            ...state,
            lastError: `DELETE_NODE failed: node ${nodeId} has children, cascade is required.`,
          },
          valid: false,
        };
      }

      Object.values(nextNodes).forEach((candidateNode) => {
        candidateNode.childIds = candidateNode.childIds.filter((childId) => !toDelete.has(childId));
        if (candidateNode.parentId && toDelete.has(candidateNode.parentId)) {
          candidateNode.parentId = null;
        }
        candidateNode.lastModified = today;
      });

      toDelete.forEach((id) => delete nextNodes[id]);

      let rootNodeId = state.rootNodeId;
      if (Object.keys(nextNodes).length === 0) {
        rootNodeId = null;
      } else if (!rootNodeId || !nextNodes[rootNodeId]) {
        const roots = collectRoots(nextNodes);
        rootNodeId = roots[0] || null;
      }

      const candidate = {
        ...state,
        nodesById: nextNodes,
        rootNodeId,
        metadata: {
          ...state.metadata,
          updatedAt: today,
        },
      };
      const resolved = applyValidated(state, candidate, 'DELETE_NODE failed validation');
      return { nextState: resolved, valid: resolved.lastError === null };
    }

    default:
      return {
        nextState: { ...state, lastError: `Unknown reducer action: ${action.type}` },
        valid: false,
      };
  }
};

export const graphReducer = (state, action) => reduceGraphAction(state, action).nextState;

export const normalizeGraphState = (raw) => {
  const base = createEmptyGraphState(raw?.metadata?.name || 'Cohort Graph Draft');
  const nodesById = {};

  if (raw && typeof raw === 'object' && raw.nodesById && typeof raw.nodesById === 'object') {
    Object.entries(raw.nodesById).forEach(([id, node]) => {
      nodesById[id] = createGraphNode(id, node || {});
    });
  } else if (raw && typeof raw === 'object' && raw.nodes && !Array.isArray(raw.nodes) && typeof raw.nodes === 'object') {
    Object.entries(raw.nodes).forEach(([id, node]) => {
      nodesById[id] = createGraphNode(id, node || {});
    });
  }

  const rootCandidates = collectRoots(nodesById);
  const rootNodeId = raw?.rootNodeId && nodesById[raw.rootNodeId] ? raw.rootNodeId : rootCandidates[0] || null;
  const breakpointNodeId = normalizeBreakpointNodeId(raw?.metadata?.breakpointNodeId, nodesById);

  return {
    nodesById,
    rootNodeId,
    metadata: {
      ...base.metadata,
      ...(raw?.metadata || {}),
      breakpointNodeId,
      updatedAt: TODAY(),
    },
    lastError: null,
  };
};

const normalizeNodeCriteriaForCard = (conditions) =>
  normalizeConditions(conditions).map((condition) => ({
    field: condition.parameterName,
    operator: condition.operator,
    value: condition.value,
    logicalOperator: condition.logicalOperator,
  }));

export const convertGraphStateToReactFlow = (graphState) => {
  const nodesById = graphState?.nodesById || {};
  const breakpointNodeId = graphState?.metadata?.breakpointNodeId || null;
  const nodeEntries = Object.values(nodesById).sort((a, b) => String(a.id).localeCompare(String(b.id)));

  const nodes = nodeEntries.map((node) => ({
    id: node.id,
    type: 'cohortNode',
    position: { x: 0, y: 0 },
    data: {
      label: node.name,
      description: node.description || '',
      status: node.status,
      patientCount: node.patientCount ?? undefined,
      isExecuted: node.isExecuted,
      criteria: {
        include: normalizeNodeCriteriaForCard(node.includeConditions),
        exclude: normalizeNodeCriteriaForCard(node.excludeConditions),
      },
      criteriaCount: {
        include: (node.includeConditions || []).length,
        exclude: (node.excludeConditions || []).length,
      },
      isBreakpoint: breakpointNodeId === node.id,
    },
  }));

  const edges = [];
  nodeEntries.forEach((node) => {
    (node.childIds || []).forEach((childId) => {
      edges.push({
        id: `e${node.id}-${childId}`,
        source: node.id,
        target: childId,
      });
    });
  });

  return { nodes, edges };
};

const toCondition = (criterion) =>
  normalizeCondition({
    parameterId: criterion?.parameterId,
    parameterName: criterion?.parameterName || criterion?.field || criterion?.name || '',
    operator: criterion?.operator || '=',
    value: criterion?.value || '',
    logicalOperator: criterion?.logicalOperator || 'AND',
  });

const criteriaFromData = (criteriaList, fallbackCount = 0, group = 'Include') => {
  if (Array.isArray(criteriaList) && criteriaList.length > 0) {
    return criteriaList.map((criterion) => toCondition(criterion));
  }
  if (Number.isFinite(fallbackCount) && fallbackCount > 0) {
    return Array.from({ length: fallbackCount }, (_, idx) =>
      toCondition({
        parameterName: `${group} condition ${idx + 1}`,
        operator: '=',
        value: '',
        logicalOperator: 'AND',
      }),
    );
  }
  return [];
};

export const buildGraphStateFromReactFlow = (nodes, edges, graphName = 'Imported Cohort Graph') => {
  const nodeById = {};
  (nodes || []).forEach((rfNode) => {
    const data = rfNode?.data || {};
    const includeConditions = criteriaFromData(data.criteria?.include, data.criteriaCount?.include, 'Include');
    const excludeConditions = criteriaFromData(data.criteria?.exclude, data.criteriaCount?.exclude, 'Exclude');
    const isExecuted = data.status === 'executed' || data.isExecuted === true || Number.isFinite(data.patientCount);

    nodeById[rfNode.id] = createGraphNode(rfNode.id, {
      name: data.label || `Cohort ${rfNode.id}`,
      includeConditions,
      excludeConditions,
      patientCount: Number.isFinite(data.patientCount) ? Number(data.patientCount) : null,
      isExecuted,
      status: data.status,
      description: data.description || '',
      isPublic: true,
    });
  });

  (edges || []).forEach((edge) => {
    const parent = nodeById[edge.source];
    const child = nodeById[edge.target];
    if (!parent || !child) return;
    if (!parent.childIds.includes(child.id)) parent.childIds.push(child.id);
    child.parentId = parent.id;
  });

  const roots = collectRoots(nodeById);
  const state = {
    nodesById: nodeById,
    rootNodeId: roots[0] || null,
    metadata: {
      name: graphName,
      createdAt: TODAY(),
      updatedAt: TODAY(),
      source: 'reactflow-import',
      breakpointNodeId: null,
    },
    lastError: null,
  };

  const validation = validateGraphState(state);
  if (!validation.valid) {
    throw new Error(`Import failed validation: ${validation.errors.join(' ')}`);
  }

  return state;
};

export const computeGraphStatistics = (graphState) => {
  const nodesById = graphState?.nodesById || {};
  const nodeIds = Object.keys(nodesById);
  const totalNodes = nodeIds.length;

  let totalPatients = 0;
  nodeIds.forEach((id) => {
    const value = nodesById[id]?.patientCount;
    if (Number.isFinite(value)) totalPatients += Number(value);
  });

  let maxDepth = 0;
  if (graphState?.rootNodeId && nodesById[graphState.rootNodeId]) {
    const queue = [{ id: graphState.rootNodeId, depth: 1 }];
    const visited = new Set();
    while (queue.length > 0) {
      const current = queue.shift();
      if (!current || visited.has(current.id)) continue;
      visited.add(current.id);
      maxDepth = Math.max(maxDepth, current.depth);
      (nodesById[current.id]?.childIds || []).forEach((childId) => {
        queue.push({ id: childId, depth: current.depth + 1 });
      });
    }
  }

  return {
    totalNodes,
    maxDepth,
    totalPatients,
  };
};

export const serializeGraphForExport = (graphState) => {
  const state = normalizeGraphState(graphState);
  const validation = validateGraphState(state);
  return {
    schemaVersion: 1,
    exportedAt: new Date().toISOString(),
    valid: validation.valid,
    validationErrors: validation.errors,
    graph: state,
    stats: computeGraphStatistics(state),
  };
};

export const deserializeImportedGraph = (raw) => {
  if (!raw || typeof raw !== 'object') {
    throw new Error('Imported JSON must be an object.');
  }

  const graphPayload = raw.graph || raw;
  if (graphPayload.nodesById || (graphPayload.nodes && !Array.isArray(graphPayload.nodes))) {
    const normalized = normalizeGraphState(graphPayload);
    const validation = validateGraphState(normalized);
    if (!validation.valid) throw new Error(validation.errors.join(' '));
    return normalized;
  }

  if (Array.isArray(graphPayload.nodes) && Array.isArray(graphPayload.edges)) {
    return buildGraphStateFromReactFlow(graphPayload.nodes, graphPayload.edges, graphPayload.metadata?.name || 'Imported Graph');
  }

  throw new Error('Unsupported graph JSON format.');
};

export const getDescendantIds = (graphState, rootId) => {
  const nodesById = graphState?.nodesById || {};
  if (!nodesById[rootId]) return [];

  const seen = new Set([rootId]);
  const stack = [rootId];
  const descendants = [];

  while (stack.length > 0) {
    const currentId = stack.pop();
    (nodesById[currentId]?.childIds || []).forEach((childId) => {
      if (seen.has(childId)) return;
      seen.add(childId);
      descendants.push(childId);
      stack.push(childId);
    });
  }

  return descendants;
};
